import { dayKey } from "./calendarDates";

export const EVENT_TYPES = ["task", "call", "email", "meeting", "note"];

// An Activity has a `due_date` but no end, so a timed event's length is
// inferred. Meetings are the exception: they carry a real ends_at, and when
// they don't, an hour is the honest guess for a call that was booked.
const DEFAULT_MINUTES = { meeting: 60, call: 30, email: 15, task: 30, note: 30 };
const MEETING_FALLBACK_MINUTES = 60;

// One colour per activity type, because in a calendar "which kind of thing is
// this" is the fastest read and the shared Badge palette makes all five
// identical (none of them is in STATUS_COLORS, so all five fall back to
// primary). Tints stay at /15 to match the Badge convention; text steps to a
// darker/lighter ramp per theme so every chip clears 4.5:1 either way.
export const TYPE_STYLE = {
  task: {
    dot: "bg-indigo-500",
    tint: "bg-indigo-500/15 border-indigo-500/30 text-indigo-700 dark:text-indigo-200",
    hover: "hover:bg-indigo-500/25",
    ring: "focus-visible:ring-indigo-500/60",
  },
  call: {
    dot: "bg-emerald-500",
    tint: "bg-emerald-500/15 border-emerald-500/30 text-emerald-700 dark:text-emerald-200",
    hover: "hover:bg-emerald-500/25",
    ring: "focus-visible:ring-emerald-500/60",
  },
  email: {
    dot: "bg-sky-500",
    tint: "bg-sky-500/15 border-sky-500/30 text-sky-700 dark:text-sky-200",
    hover: "hover:bg-sky-500/25",
    ring: "focus-visible:ring-sky-500/60",
  },
  meeting: {
    dot: "bg-violet-500",
    tint: "bg-violet-500/15 border-violet-500/30 text-violet-700 dark:text-violet-200",
    hover: "hover:bg-violet-500/25",
    ring: "focus-visible:ring-violet-500/60",
  },
  note: {
    dot: "bg-amber-500",
    tint: "bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-200",
    hover: "hover:bg-amber-500/25",
    ring: "focus-visible:ring-amber-500/60",
  },
};

export function typeStyle(type) {
  return TYPE_STYLE[type] || TYPE_STYLE.task;
}

// Black/white with an explicit per-theme alpha, NOT `bg-surface/20`. The theme
// tokens (bg/surface/border/txt/muted) are `var(--…)` values, and Tailwind 3
// cannot fold an alpha into a var() colour -- it silently emits no rule at all,
// so every `bg-surface/20` in this codebase is a no-op. Verified by grepping the
// compiled CSS: 16 such classes in source, zero matching rules in the bundle.
export const WEEKEND_TINT = "bg-black/[0.035] dark:bg-white/[0.045]";

// Hover wash for the large click-to-create surfaces (month cells, the all-day
// row). `hover:bg-surface` is the app's idiom for buttons and works there, but
// on a grid cell in the light theme it's a 1-step change nobody can see -- and
// a cell whose click creates an event needs to say so on hover.
//
// Deliberately ~2x WEEKEND_TINT rather than a hair above it: both set
// background-color, so hovering a weekend cell replaces 0.035 with this value
// instead of adding to it. At 0.045 that was a two-255ths change -- measured,
// not guessed -- i.e. no hover feedback at all on Saturdays and Sundays.
export const CELL_HOVER = "hover:bg-black/[0.07] dark:hover:bg-white/[0.09]";

/**
 * Is this instant a date without a time?
 *
 * Both arms are needed, and the UTC one is not paranoia. A date-only entry
 * reaches the calendar as midnight in one of two zones depending on which
 * screen created it: this page's all-day path writes *local* midnight
 * (`new Date("2026-08-14T00:00").toISOString()`), while the Activities page
 * posts a bare "2026-08-14" that the API parses as a naive datetime and
 * Postgres stores in a `timestamptz` column as midnight *UTC*. Without the UTC
 * arm, every task ever created on that page reads as a timed 02:00 event for a
 * UTC+2 viewer -- stacked on the 2 AM line, where nobody scrolls -- instead of
 * sitting in the all-day row. Caught by looking at real seeded data, not by
 * reading the code.
 *
 * The cost is that a genuine 00:00-UTC appointment is treated as all-day. That
 * trade is deliberate: it degrades to "shown in the all-day row", and 02:00
 * meetings are rarer than dated tasks by orders of magnitude.
 */
function midnightKind(d) {
  const local = d.getHours() === 0 && d.getMinutes() === 0;
  const utc = d.getUTCHours() === 0 && d.getUTCMinutes() === 0;
  if (local) return "local";
  if (utc) return "utc";
  return null;
}

/**
 * Fold the two API shapes into one event list.
 *
 * Activities are the anchor for everything on the calendar (that's the
 * documented model: a Meeting is an extension of an Activity(type="meeting"),
 * and the router keeps Activity.due_date in sync with Meeting.starts_at).
 * Meetings are joined in only for what the anchor can't express: a real end
 * time, and the meeting id to link and reschedule through.
 *
 * "Timed" is decided by the local clock time being non-midnight, because the
 * existing activity form only ever submits a date -- so an untimed task lands
 * at 00:00 and belongs in the all-day row, not stacked on the midnight line
 * where nobody scrolls. A user who genuinely wants midnight can pick 00:01.
 */
export function buildEvents(activities, meetings) {
  const meetingByActivity = new Map();
  for (const m of meetings || []) {
    if (m.activity_id) meetingByActivity.set(m.activity_id, m);
  }

  const events = [];
  for (const a of activities || []) {
    if (!a.due_date) continue;
    const meeting = meetingByActivity.get(a.id) || null;

    let start = new Date(meeting?.starts_at || a.due_date);
    if (Number.isNaN(start.getTime())) continue;

    const parsedEnd = meeting?.ends_at ? new Date(meeting.ends_at) : null;
    const explicitEnd = parsedEnd && !Number.isNaN(parsedEnd.getTime()) && parsedEnd > start
      ? parsedEnd
      : null;
    const midnight = midnightKind(start);

    let end;
    let allDay = false;
    if (midnight && !explicitEnd) {
      allDay = true;
      // Normalised to local midnight of the date that was actually meant, so
      // everything downstream (day bucketing, the popover's date, a drag's new
      // date) sees one clean shape instead of re-deriving the intent.
      if (midnight === "utc") {
        start = new Date(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
      }
      end = start;
    } else if (meeting?.starts_at) {
      end = explicitEnd || new Date(start.getTime() + MEETING_FALLBACK_MINUTES * 60000);
    } else {
      const minutes = DEFAULT_MINUTES[a.type] ?? 30;
      end = new Date(start.getTime() + minutes * 60000);
    }

    events.push({
      id: a.id,
      activity: a,
      meeting,
      meetingId: meeting?.id || null,
      type: a.type || "task",
      subject: a.subject,
      description: a.description || "",
      completed: !!a.completed,
      start,
      end,
      allDay,
      key: dayKey(start),
      // A meeting whose end runs past midnight would otherwise be clipped
      // to a 0-height block on the following day; the time grid draws it on
      // the start day only, capped at 24:00.
      endsNextDay: !allDay && dayKey(end) !== dayKey(start),
    });
  }
  return events;
}

export function filterEvents(events, { types, hideCompleted }) {
  const active = new Set(types && types.length ? types : EVENT_TYPES);
  return events.filter(
    (e) => active.has(e.type) && !(hideCompleted && e.completed)
  );
}

/** Bucket events by local day key, timed ones sorted by start. */
export function bucketByDay(events) {
  const map = new Map();
  for (const e of events) {
    if (!map.has(e.key)) map.set(e.key, []);
    map.get(e.key).push(e);
  }
  for (const list of map.values()) {
    list.sort(
      (a, b) =>
        Number(b.allDay) - Number(a.allDay) ||
        a.start - b.start ||
        a.subject.localeCompare(b.subject)
    );
  }
  return map;
}
